import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import axios from 'axios';
import { ArrowLeft, ArrowRight, Loader2, Link2, ChevronRight, Lock, AlertTriangle, TrendingUp, Target, CheckCircle, BarChart3, Globe, User, Mail, Phone } from 'lucide-react';
import { SITE_URL, SITE_NAME, DEFAULT_OG_IMAGE, breadcrumbSchema } from '../lib/seoConstants';

const API = process.env.REACT_APP_BACKEND_URL;

const T = {
  ar: {
    freeAnalysis: 'تحليل مجاني',
    heroTitle1: 'اكتشف ما ينقص',
    heroHighlight: 'منصتك',
    heroSub: 'تقرير تحليلي بنقاط الضعف وخطة تحسين واضحة خلال دقائق',
    startNow: 'ابدأ التحليل',
    back: 'رجوع',
    questionOf: (n, total) => `${n} من ${total}`,
    enterUrl: 'رابط المنصة',
    urlOptional: 'أدخل الرابط للحصول على تحليل أدق (اختياري)',
    skip: 'تخطي',
    next: 'التالي',
    analyzeBtn: 'حلّل منصتي',
    leadTitle: 'أين نُرسل التقرير؟',
    leadSub: 'أدخل بياناتك لاستلام التقرير مع توصيات إضافية',
    leadName: 'الاسم الكامل',
    leadEmail: 'البريد الإلكتروني',
    leadPhone: 'رقم التواصل',
    leadSubmit: 'أرسل التقرير',
    leadRequired: 'الاسم والبريد الإلكتروني مطلوبان',
    loading: 'جارٍ تحليل المنصة...',
    loadingSub: 'نُراجع بياناتك ونُجهّز تقريراً مخصصاً',
    error: 'حدث خطأ أثناء التحليل. يرجى المحاولة مرة أخرى.',
    scoreLabel: 'تقييم المنصة',
    scoreUX: 'تجربة المستخدم',
    scoreVC: 'وضوح القيمة',
    scoreCR: 'جاهزية التحويل',
    problemsLabel: 'المشكلات الرئيسية',
    oppsLabel: 'فرص التحسين',
    goalLabel: 'تحليل مرتبط بهدفك',
    lockedTitle: 'رصدنا نقاطاً إضافية تؤثر على أداء منصتك',
    lockedSub: 'تقرير مفصّل مع خطة تطوير عملية قابلة للتنفيذ',
    unlockBtn: 'احصل على الخطة الكاملة',
    ctaBtn: 'أريد خطة تطوير شاملة',
    restart: 'أعد التحليل',
    confirmTitle: 'تم استلام طلبك بنجاح',
    confirmMsg: 'سيتواصل معك أحد أعضاء فريقنا عبر رقم التواصل والبريد الإلكتروني المُسجّل لمناقشة خطة تطوير منصتك.',
    confirmThank: 'شكراً لثقتك بنيكسا',
    confirmRestart: 'تحليل منصة أخرى',
    confirmHome: 'العودة للرئيسية',
    steps: [
      {
        question: 'ما نوع منصتك؟',
        key: 'platform_type',
        options: [
          { value: 'موقع', icon: '🌐', label: 'موقع إلكتروني' },
          { value: 'متجر', icon: '🛒', label: 'متجر إلكتروني' },
          { value: 'تطبيق', icon: '📱', label: 'تطبيق جوال' },
          { value: 'SaaS', icon: '☁️', label: 'منصة SaaS' },
        ],
      },
      {
        question: 'ما هدفك الأساسي؟',
        key: 'goal',
        options: [
          { value: 'زيادة المبيعات', icon: '💰', label: 'زيادة المبيعات' },
          { value: 'جذب عملاء', icon: '🧲', label: 'استقطاب عملاء' },
          { value: 'بناء براند', icon: '⭐', label: 'بناء العلامة التجارية' },
          { value: 'إطلاق فكرة', icon: '🚀', label: 'إطلاق منتج جديد' },
        ],
      },
      {
        question: 'ما التحدي الأبرز؟',
        key: 'problem',
        options: [
          { value: 'ما في مبيعات', icon: '📉', label: 'لا توجد مبيعات' },
          { value: 'زوار بدون تفاعل', icon: '👻', label: 'زوار بدون تفاعل' },
          { value: 'الموقع ما يقنع', icon: '😐', label: 'المنصة غير مُقنعة' },
          { value: 'مو عارف المشكلة', icon: '❓', label: 'غير محدد' },
        ],
      },
      {
        question: 'مرحلة المنصة الحالية:',
        key: 'stage',
        options: [
          { value: 'جديدة', icon: '🌱', label: 'في البداية' },
          { value: 'شغالة بس النتائج ضعيفة', icon: '📊', label: 'تعمل، لكن النتائج ضعيفة' },
          { value: 'شغالة وأبغى أطورها', icon: '🔧', label: 'تعمل وأريد تطويرها' },
        ],
      },
    ],
  },
  en: {
    freeAnalysis: 'Free Analysis',
    heroTitle1: 'Find what your',
    heroHighlight: 'platform',
    heroTitle2: ' is missing',
    heroSub: 'Discover weaknesses and get a clear improvement plan in minutes',
    startNow: 'Start Now',
    back: 'Back',
    questionOf: (n, total) => `Question ${n} of ${total}`,
    enterUrl: 'Enter your platform URL',
    urlOptional: 'For a more precise analysis (optional)',
    skip: 'Skip',
    next: 'Next',
    analyzeBtn: 'Analyze My Platform',
    // Lead capture
    leadTitle: 'Where should we send the report?',
    leadSub: 'Enter your details and we\'ll send you the report + additional insights',
    leadName: 'Full Name',
    leadEmail: 'Email Address',
    leadPhone: 'Phone Number',
    leadSubmit: 'Send Me the Report',
    leadRequired: 'Name and email are required',
    // Loading & Results
    loading: 'Analyzing your platform...',
    loadingSub: 'We are reviewing your answers and preparing a custom report',
    error: 'An error occurred during analysis. Please try again.',
    scoreLabel: 'Platform Score',
    scoreUX: 'User Experience',
    scoreVC: 'Value Clarity',
    scoreCR: 'Conversion Readiness',
    problemsLabel: 'Key Problems',
    oppsLabel: 'Improvement Opportunities',
    goalLabel: 'Goal-based Analysis',
    lockedTitle: 'We found additional issues impacting your platform',
    lockedSub: 'Detailed report with an actionable development plan',
    unlockBtn: 'Get the Full Plan',
    ctaBtn: 'I Want a Full Development Plan',
    restart: 'Restart Analysis',
    // Confirmation
    confirmTitle: 'Your request has been received!',
    confirmMsg: 'Our team will reach out to you shortly via the phone number and email you provided to discuss your platform development plan in detail.',
    confirmThank: 'Thank you for trusting Nexa',
    confirmRestart: 'Analyze Another Platform',
    confirmHome: 'Back to Home',
    steps: [
      {
        question: 'What type is your platform?',
        key: 'platform_type',
        options: [
          { value: 'Website', icon: '🌐', label: 'Website' },
          { value: 'E-commerce', icon: '🛒', label: 'E-commerce' },
          { value: 'Mobile App', icon: '📱', label: 'Mobile App' },
          { value: 'SaaS', icon: '☁️', label: 'SaaS' },
        ],
      },
      {
        question: 'What is your goal?',
        key: 'goal',
        options: [
          { value: 'Increase sales', icon: '💰', label: 'Increase Sales' },
          { value: 'Attract clients', icon: '🧲', label: 'Attract Clients' },
          { value: 'Build brand', icon: '⭐', label: 'Build Brand' },
          { value: 'Launch idea', icon: '🚀', label: 'Launch Idea' },
        ],
      },
      {
        question: 'Where is the problem?',
        key: 'problem',
        options: [
          { value: 'No sales', icon: '📉', label: 'No Sales' },
          { value: 'Visitors without engagement', icon: '👻', label: 'No Engagement' },
          { value: 'Site not convincing', icon: '😐', label: 'Not Convincing' },
          { value: 'Not sure', icon: '❓', label: 'Not Sure' },
        ],
      },
      {
        question: 'Platform stage:',
        key: 'stage',
        options: [
          { value: 'New', icon: '🌱', label: 'New' },
          { value: 'Running but weak results', icon: '📊', label: 'Running, Weak Results' },
          { value: 'Running and want to improve', icon: '🔧', label: 'Want to Improve' },
        ],
      },
    ],
  },
};

// Steps: -1=landing, 0-3=questions, 4=url, 5=lead capture, 6=loading, 7=results

const ScoreRing = ({ score, label, color }) => {
  const radius = 36;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (score / 100) * circumference;

  return (
    <div className="flex flex-col items-center gap-2">
      <div className="relative w-24 h-24">
        <svg className="w-24 h-24 -rotate-90" viewBox="0 0 80 80">
          <circle cx="40" cy="40" r={radius} fill="none" stroke="rgba(255,255,255,0.05)" strokeWidth="5" />
          <motion.circle
            cx="40" cy="40" r={radius} fill="none" stroke={color} strokeWidth="5" strokeLinecap="round"
            strokeDasharray={circumference} strokeDashoffset={circumference}
            animate={{ strokeDashoffset: offset }}
            transition={{ duration: 1.5, delay: 0.3, ease: "easeOut" }}
          />
        </svg>
        <div className="absolute inset-0 flex items-center justify-center">
          <span className="text-xl font-bold text-white">{score}</span>
        </div>
      </div>
      <span className="text-xs text-white/50 text-center">{label}</span>
    </div>
  );
};

const AnalyzePage = () => {
  const [lang, setLang] = useState('ar');
  const [step, setStep] = useState(-1);
  const [answers, setAnswers] = useState({ platform_type: '', goal: '', problem: '', stage: '', url: '' });
  const [lead, setLead] = useState({ name: '', email: '', phone: '' });
  const [leadError, setLeadError] = useState('');
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [disabled, setDisabled] = useState(false);
  const [checkingStatus, setCheckingStatus] = useState(true);

  const t = T[lang];
  const isRTL = lang === 'ar';
  const dir = isRTL ? 'rtl' : 'ltr';

  // Check if analyzer is enabled
  React.useEffect(() => {
    axios.get(`${API}/api/analyzer-status`).then(({ data }) => {
      setDisabled(!data.enabled);
      setCheckingStatus(false);
    }).catch(() => setCheckingStatus(false));
  }, []);

  const toggleLang = () => setLang(prev => prev === 'ar' ? 'en' : 'ar');

  const selectAnswer = (key, value) => {
    setAnswers(prev => ({ ...prev, [key]: value }));
    if (step < 3) {
      setTimeout(() => setStep(step + 1), 300);
    } else {
      setTimeout(() => setStep(4), 300);
    }
  };

  const goToLeadCapture = () => {
    setStep(5);
  };

  const submitAnalysis = async () => {
    if (!lead.name.trim() || !lead.email.trim()) {
      setLeadError(t.leadRequired);
      return;
    }
    setLeadError('');
    setStep(6);
    setError('');
    try {
      const { data } = await axios.post(`${API}/api/analyze`, {
        ...answers,
        language: lang,
        lead_name: lead.name,
        lead_email: lead.email,
        lead_phone: lead.phone,
      });
      setResult(data);
      setStep(7);
    } catch {
      setError(t.error);
      setStep(5);
    }
  };

  const restart = () => {
    setStep(-1);
    setAnswers({ platform_type: '', goal: '', problem: '', stage: '', url: '' });
    setLead({ name: '', email: '', phone: '' });
    setResult(null);
  };

  const totalSteps = 6;
  const progress = step >= 0 && step <= 5 ? ((step + 1) / totalSteps) * 100 : 0;

  const BackArrow = isRTL ? ArrowRight : ArrowLeft;

  return (
    <div className="min-h-screen bg-[#00040A] text-white relative overflow-hidden" dir={dir} style={{ fontFamily: isRTL ? "'Tajawal', 'IBM Plex Sans Arabic', sans-serif" : "'Inter', sans-serif" }}>
      <Helmet>
        <html lang={isRTL ? 'ar' : 'en'} dir={dir} />
        <title>{isRTL ? 'تحليل منصتك المجاني | نيكسا سولوشينز' : 'Free Platform Analysis | Nexa Solutions'}</title>
        <meta name="description" content={isRTL
          ? 'احصل على تحليل مجاني لمنصتك مع تقرير مفصل بنقاط الضعف وخطة تحسين واضحة خلال دقائق من فريق نيكسا سولوشينز.'
          : 'Get a free AI-powered analysis of your platform with a detailed report of weaknesses and a clear improvement plan in minutes from Nexa Solutions.'} />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href={`${SITE_URL}/analyze`} />
        <link rel="alternate" hrefLang="ar" href={`${SITE_URL}/analyze`} />
        <link rel="alternate" hrefLang="en" href={`${SITE_URL}/analyze`} />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content={SITE_NAME} />
        <meta property="og:title" content={isRTL ? 'تحليل منصتك المجاني | نيكسا سولوشينز' : 'Free Platform Analysis | Nexa Solutions'} />
        <meta property="og:description" content={isRTL
          ? 'احصل على تحليل مجاني لمنصتك مع تقرير مفصل بنقاط الضعف وخطة تحسين واضحة.'
          : 'Get a free AI-powered analysis of your platform with a detailed improvement plan.'} />
        <meta property="og:url" content={`${SITE_URL}/analyze`} />
        <meta property="og:image" content={DEFAULT_OG_IMAGE} />
        <meta property="og:locale" content={isRTL ? 'ar_SA' : 'en_US'} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={isRTL ? 'تحليل منصتك المجاني | نيكسا سولوشينز' : 'Free Platform Analysis | Nexa Solutions'} />
        <meta name="twitter:image" content={DEFAULT_OG_IMAGE} />
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbSchema([
            { name: 'Home', url: '/' },
            { name: 'Platform Analysis', url: '/analyze' },
          ]))}
        </script>
      </Helmet>
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-1/4 left-1/2 -translate-x-1/2 w-[800px] h-[800px] bg-[#00C2FF]/[0.03] rounded-full blur-[200px]" />
      </div>

      {/* Header */}
      <header className="relative z-20 p-6 flex items-center justify-between">
        <a href="/" className="flex items-center gap-2">
          <img src="https://customer-assets.emergentagent.com/job_9ff69888-dc97-4485-a373-d9d7220b18c1/artifacts/tvnfbiha_Nexa_logo%20icon%20Cyan.svg" alt="Nexa" className="h-8 w-8" />
          <span className="text-white font-bold text-lg tracking-wider">NEXA</span>
        </a>
        <div className="flex items-center gap-3">
          {step > 0 && step < 6 && (
            <button data-testid="back-btn" onClick={() => setStep(step - 1)} className="flex items-center gap-1 text-sm text-white/40 hover:text-white transition-colors">
              <BackArrow className="w-4 h-4" /> {t.back}
            </button>
          )}
          <motion.button
            data-testid="analyze-lang-toggle"
            onClick={toggleLang}
            className="relative px-4 py-2 text-xs tracking-widest uppercase border border-white/10 rounded-full hover:border-[#00C2FF]/40 transition-all duration-300 flex items-center gap-2"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <Globe className="w-3.5 h-3.5 text-white/40" />
            <span className={`transition-colors duration-300 ${lang === 'en' ? 'text-[#00C2FF]' : 'text-white/40'}`}>EN</span>
            <span className="text-white/20">/</span>
            <span className={`transition-colors duration-300 ${lang === 'ar' ? 'text-[#00C2FF]' : 'text-white/40'}`}>AR</span>
          </motion.button>
        </div>
      </header>

      {/* Progress bar */}
      {step >= 0 && step <= 5 && (
        <div className="relative z-20 px-6 mb-8">
          <div className="max-w-xl mx-auto h-1 bg-white/5 rounded-full overflow-hidden">
            <motion.div className="h-full bg-[#00C2FF] rounded-full" animate={{ width: `${progress}%` }} transition={{ duration: 0.3 }} />
          </div>
        </div>
      )}

      <main className="relative z-10 flex items-center justify-center min-h-[70vh] px-6">
        {checkingStatus ? (
          <div className="text-center">
            <div className="w-8 h-8 border-2 border-[#00C2FF]/20 border-t-[#00C2FF] rounded-full animate-spin mx-auto" />
          </div>
        ) : disabled ? (
          <motion.div className="text-center max-w-md mx-auto" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <div className="w-16 h-16 bg-white/5 rounded-2xl flex items-center justify-center mx-auto mb-6">
              <BarChart3 className="w-8 h-8 text-white/20" />
            </div>
            <h2 className="text-2xl font-bold text-white mb-3">{lang === 'ar' ? 'الخدمة غير متاحة حالياً' : 'Service Currently Unavailable'}</h2>
            <p className="text-sm text-white/40 mb-8">{lang === 'ar' ? 'أداة التحليل معطلة مؤقتاً. يرجى العودة لاحقاً.' : 'The analysis tool is temporarily disabled. Please check back later.'}</p>
            <motion.a href="/" className="inline-flex items-center gap-2 bg-[#00C2FF] text-[#00040A] font-bold px-8 py-3 rounded-full hover:bg-white transition-colors text-sm" whileHover={{ scale: 1.03 }}>
              {lang === 'ar' ? 'العودة للرئيسية' : 'Back to Home'}
            </motion.a>
          </motion.div>
        ) : (
        <AnimatePresence mode="wait">
          {/* LANDING */}
          {step === -1 && (
            <motion.div key="landing" className="text-center max-w-2xl mx-auto" initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -30 }}>
              <div className="inline-flex items-center gap-2 bg-[#00C2FF]/10 border border-[#00C2FF]/20 rounded-full px-4 py-1.5 text-[#00C2FF] text-xs tracking-wider mb-8">
                <BarChart3 className="w-3.5 h-3.5" /> {t.freeAnalysis}
              </div>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
                {t.heroTitle1} <span className="text-[#00C2FF]">{t.heroHighlight}</span>{t.heroTitle2 || ''}
              </h1>
              <p className="text-lg text-white/40 mb-12 max-w-lg mx-auto leading-relaxed">{t.heroSub}</p>
              <motion.button data-testid="start-analysis-btn" onClick={() => setStep(0)} className="bg-[#00C2FF] text-[#00040A] font-bold text-lg px-10 py-4 rounded-full hover:bg-white transition-colors" whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }}>
                {t.startNow}
              </motion.button>
            </motion.div>
          )}

          {/* QUESTIONS */}
          {step >= 0 && step <= 3 && (
            <motion.div key={`q-${step}-${lang}`} className="w-full max-w-xl mx-auto" initial={{ opacity: 0, x: isRTL ? 30 : -30 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: isRTL ? -30 : 30 }} transition={{ duration: 0.3 }}>
              <p className="text-xs text-[#00C2FF] tracking-wider mb-3">{t.questionOf(step + 1, totalSteps)}</p>
              <h2 className="text-2xl sm:text-3xl font-bold mb-10">{t.steps[step].question}</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {t.steps[step].options.map(opt => (
                  <motion.button key={opt.value} data-testid={`option-${opt.value}`} onClick={() => selectAnswer(t.steps[step].key, opt.value)}
                    className={`p-5 rounded-2xl border text-start transition-all group ${answers[t.steps[step].key] === opt.value ? 'bg-[#00C2FF]/10 border-[#00C2FF]/40' : 'bg-white/[0.02] border-white/[0.06] hover:border-white/20 hover:bg-white/[0.04]'}`}
                    whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                    <span className="text-2xl mb-2 block">{opt.icon}</span>
                    <span className="text-base font-medium text-white">{opt.label}</span>
                  </motion.button>
                ))}
              </div>
            </motion.div>
          )}

          {/* URL STEP */}
          {step === 4 && (
            <motion.div key={`url-${lang}`} className="w-full max-w-xl mx-auto" initial={{ opacity: 0, x: isRTL ? 30 : -30 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: isRTL ? -30 : 30 }}>
              <p className="text-xs text-[#00C2FF] tracking-wider mb-3">{t.questionOf(5, totalSteps)} ({isRTL ? 'اختياري' : 'optional'})</p>
              <h2 className="text-2xl sm:text-3xl font-bold mb-4">{t.enterUrl}</h2>
              <p className="text-sm text-white/30 mb-8">{t.urlOptional}</p>
              <div className="relative mb-8">
                <Link2 className={`absolute top-1/2 -translate-y-1/2 w-5 h-5 text-white/20 ${isRTL ? 'right-4' : 'left-4'}`} />
                <input data-testid="url-input" type="url" value={answers.url} onChange={(e) => setAnswers(prev => ({ ...prev, url: e.target.value }))} placeholder="https://example.com" dir="ltr"
                  className="w-full bg-white/[0.03] border border-white/10 rounded-2xl py-4 px-12 text-white text-base placeholder:text-white/20 focus:border-[#00C2FF]/40 focus:ring-0 outline-none transition-colors" />
              </div>
              <div className="flex gap-3">
                <motion.button data-testid="skip-url-btn" onClick={() => { setAnswers(prev => ({ ...prev, url: '' })); goToLeadCapture(); }}
                  className="flex-1 py-4 rounded-2xl border border-white/10 text-white/50 text-sm font-medium hover:border-white/20 transition-colors" whileTap={{ scale: 0.98 }}>
                  {t.skip}
                </motion.button>
                <motion.button data-testid="next-url-btn" onClick={goToLeadCapture}
                  className="flex-1 py-4 rounded-2xl bg-[#00C2FF] text-[#00040A] font-bold text-sm hover:bg-white transition-colors flex items-center justify-center gap-2" whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                  {t.next}
                </motion.button>
              </div>
            </motion.div>
          )}

          {/* LEAD CAPTURE STEP */}
          {step === 5 && (
            <motion.div key={`lead-${lang}`} className="w-full max-w-xl mx-auto" initial={{ opacity: 0, x: isRTL ? 30 : -30 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: isRTL ? -30 : 30 }}>
              <p className="text-xs text-[#00C2FF] tracking-wider mb-3">{t.questionOf(6, totalSteps)}</p>
              <h2 className="text-2xl sm:text-3xl font-bold mb-3">{t.leadTitle}</h2>
              <p className="text-sm text-white/30 mb-8">{t.leadSub}</p>

              <div className="space-y-4 mb-8">
                <div className="relative">
                  <User className={`absolute top-1/2 -translate-y-1/2 w-5 h-5 text-white/20 ${isRTL ? 'right-4' : 'left-4'}`} />
                  <input data-testid="lead-name-input" type="text" value={lead.name} onChange={(e) => setLead(prev => ({ ...prev, name: e.target.value }))} placeholder={t.leadName}
                    className={`w-full bg-white/[0.03] border border-white/10 rounded-2xl py-4 text-white text-base placeholder:text-white/20 focus:border-[#00C2FF]/40 focus:ring-0 outline-none transition-colors ${isRTL ? 'pr-12 pl-4' : 'pl-12 pr-4'}`} />
                </div>
                <div className="relative">
                  <Mail className={`absolute top-1/2 -translate-y-1/2 w-5 h-5 text-white/20 ${isRTL ? 'right-4' : 'left-4'}`} />
                  <input data-testid="lead-email-input" type="email" value={lead.email} onChange={(e) => setLead(prev => ({ ...prev, email: e.target.value }))} placeholder={t.leadEmail} dir="ltr"
                    className="w-full bg-white/[0.03] border border-white/10 rounded-2xl py-4 px-12 text-white text-base placeholder:text-white/20 focus:border-[#00C2FF]/40 focus:ring-0 outline-none transition-colors" />
                </div>
                <div className="relative">
                  <Phone className={`absolute top-1/2 -translate-y-1/2 w-5 h-5 text-white/20 ${isRTL ? 'right-4' : 'left-4'}`} />
                  <input data-testid="lead-phone-input" type="tel" value={lead.phone} onChange={(e) => setLead(prev => ({ ...prev, phone: e.target.value }))} placeholder={t.leadPhone} dir="ltr"
                    className="w-full bg-white/[0.03] border border-white/10 rounded-2xl py-4 px-12 text-white text-base placeholder:text-white/20 focus:border-[#00C2FF]/40 focus:ring-0 outline-none transition-colors" />
                </div>
              </div>

              {leadError && (
                <div className="mb-6 p-3 bg-red-500/10 border border-red-500/20 rounded-xl text-red-400 text-sm">{leadError}</div>
              )}
              {error && (
                <div className="mb-6 p-3 bg-red-500/10 border border-red-500/20 rounded-xl text-red-400 text-sm">{error}</div>
              )}

              <motion.button data-testid="submit-lead-btn" onClick={submitAnalysis}
                className="w-full py-4 rounded-2xl bg-[#00C2FF] text-[#00040A] font-bold text-base hover:bg-white transition-colors flex items-center justify-center gap-2" whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                {t.leadSubmit}
              </motion.button>
            </motion.div>
          )}

          {/* LOADING */}
          {step === 6 && (
            <motion.div key="loading" className="text-center" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
              <motion.div className="w-20 h-20 border-2 border-[#00C2FF]/20 border-t-[#00C2FF] rounded-full mx-auto mb-8" animate={{ rotate: 360 }} transition={{ duration: 1.5, repeat: Infinity, ease: "linear" }} />
              <h2 className="text-2xl font-bold mb-3">{t.loading}</h2>
              <p className="text-white/30 text-sm">{t.loadingSub}</p>
            </motion.div>
          )}

          {/* RESULTS */}
          {step === 7 && result && (
            <motion.div key={`results-${lang}`} className="w-full max-w-3xl mx-auto" initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }}>
              {/* Scores */}
              {result.scores && (
                <motion.div className="bg-white/[0.02] border border-white/[0.06] rounded-3xl p-8 mb-6" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
                  <h3 className="text-sm text-white/40 mb-6 flex items-center gap-2">
                    <Target className="w-4 h-4 text-[#00C2FF]" /> {t.scoreLabel}
                  </h3>
                  <div className="flex justify-around">
                    <ScoreRing score={result.scores.user_experience} label={t.scoreUX} color="#00C2FF" />
                    <ScoreRing score={result.scores.value_clarity} label={t.scoreVC} color="#8B5CF6" />
                    <ScoreRing score={result.scores.conversion_readiness} label={t.scoreCR} color="#10B981" />
                  </div>
                </motion.div>
              )}

              {/* Problems */}
              <motion.div className="bg-white/[0.02] border border-white/[0.06] rounded-3xl p-8 mb-6" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }}>
                <h3 className="text-sm text-white/40 mb-6 flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4 text-red-400" /> {t.problemsLabel}
                </h3>
                <div className="space-y-4">
                  {result.problems?.map((p, i) => (
                    <div key={i} className="p-4 bg-red-500/5 border border-red-500/10 rounded-2xl">
                      <div className="flex items-center gap-2 mb-1.5">
                        <span className="text-base font-semibold text-white">{p.title}</span>
                        {p.impact && <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold ${['عالي', 'High'].includes(p.impact) ? 'bg-red-500/20 text-red-400' : 'bg-amber-500/20 text-amber-400'}`}>{p.impact}</span>}
                      </div>
                      <p className="text-sm text-white/40 leading-relaxed">{p.description}</p>
                    </div>
                  ))}
                </div>
              </motion.div>

              {/* Opportunities */}
              <motion.div className="bg-white/[0.02] border border-white/[0.06] rounded-3xl p-8 mb-6" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.6 }}>
                <h3 className="text-sm text-white/40 mb-6 flex items-center gap-2">
                  <TrendingUp className="w-4 h-4 text-emerald-400" /> {t.oppsLabel}
                </h3>
                <div className="space-y-4">
                  {result.opportunities?.map((o, i) => (
                    <div key={i} className="p-4 bg-emerald-500/5 border border-emerald-500/10 rounded-2xl">
                      <span className="text-base font-semibold text-white block mb-1.5">{o.title}</span>
                      <p className="text-sm text-white/40 leading-relaxed">{o.description}</p>
                    </div>
                  ))}
                </div>
              </motion.div>

              {/* Goal Analysis */}
              {result.goal_analysis && (
                <motion.div className="bg-white/[0.02] border border-white/[0.06] rounded-3xl p-8 mb-6" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.8 }}>
                  <h3 className="text-sm text-white/40 mb-4 flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-[#00C2FF]" /> {t.goalLabel}
                  </h3>
                  <p className="text-base text-white/60 leading-relaxed">{result.goal_analysis}</p>
                </motion.div>
              )}

              {/* Locked Section */}
              {result.has_locked_content && (
                <motion.div className="relative rounded-3xl p-8 mb-6 overflow-hidden" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1 }}>
                  <div className="absolute inset-0 bg-white/[0.02] border border-white/[0.06] rounded-3xl" />
                  <div className="absolute inset-0 backdrop-blur-sm bg-[#00040A]/50 rounded-3xl z-10" />
                  <div className="blur-sm opacity-40 space-y-3 mb-6">
                    {result.locked_problems?.map((p, i) => (
                      <div key={i} className="p-3 bg-white/5 rounded-xl"><span className="text-sm text-white">{p.title}</span></div>
                    ))}
                    {result.locked_opportunities?.map((o, i) => (
                      <div key={i} className="p-3 bg-white/5 rounded-xl"><span className="text-sm text-white">{o.title}</span></div>
                    ))}
                  </div>
                  <div className="relative z-20 text-center">
                    <div className="w-14 h-14 bg-[#00C2FF]/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
                      <Lock className="w-6 h-6 text-[#00C2FF]" />
                    </div>
                    <h3 className="text-lg font-bold text-white mb-2">{t.lockedTitle}</h3>
                    <p className="text-sm text-white/30 mb-6">{t.lockedSub}</p>
                    <motion.button onClick={() => setStep(8)} data-testid="unlock-report-btn" className="inline-flex items-center gap-2 bg-[#00C2FF] text-[#00040A] font-bold px-8 py-3 rounded-full hover:bg-white transition-colors" whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }}>
                      {t.unlockBtn}
                    </motion.button>
                  </div>
                </motion.div>
              )}

              {/* CTA */}
              <motion.div className="text-center mt-8 space-y-4" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.2 }}>
                <motion.button onClick={() => setStep(8)} data-testid="contact-cta-btn" className="inline-flex items-center gap-2 bg-[#00C2FF] text-[#00040A] font-bold px-10 py-4 rounded-full hover:bg-white transition-colors text-base" whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }}>
                  {t.ctaBtn}
                </motion.button>
                <div>
                  <button data-testid="restart-btn" onClick={restart} className="text-sm text-white/30 hover:text-white/60 transition-colors">{t.restart}</button>
                </div>
              </motion.div>
            </motion.div>
          )}

          {/* CONFIRMATION */}
          {step === 8 && (
            <motion.div key={`confirm-${lang}`} className="text-center max-w-xl mx-auto" initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0 }}>
              <motion.div
                className="w-20 h-20 bg-[#00C2FF]/10 rounded-full flex items-center justify-center mx-auto mb-8"
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ type: "spring", stiffness: 200, delay: 0.2 }}
              >
                <CheckCircle className="w-10 h-10 text-[#00C2FF]" />
              </motion.div>
              <motion.h2
                className="text-3xl sm:text-4xl font-bold mb-4"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
              >
                {t.confirmTitle}
              </motion.h2>
              <motion.p
                className="text-base text-white/50 leading-relaxed mb-4 max-w-md mx-auto"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.45 }}
              >
                {t.confirmMsg}
              </motion.p>
              <motion.p
                className="text-sm text-[#00C2FF]/60 mb-10"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.6 }}
              >
                {t.confirmThank}
              </motion.p>
              <motion.div
                className="flex flex-col sm:flex-row items-center justify-center gap-4"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.7 }}
              >
                <motion.button data-testid="confirm-restart-btn" onClick={restart} className="px-8 py-3 rounded-full border border-white/10 text-white/50 text-sm font-medium hover:border-[#00C2FF]/40 hover:text-white transition-all" whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }}>
                  {t.confirmRestart}
                </motion.button>
                <motion.a href="/" data-testid="confirm-home-btn" className="px-8 py-3 rounded-full bg-[#00C2FF] text-[#00040A] font-bold text-sm hover:bg-white transition-colors" whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }}>
                  {t.confirmHome}
                </motion.a>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
        )}
      </main>
    </div>
  );
};

export default AnalyzePage;
